/** 申請フロー共有UI: 進捗ドット・状態バッジ・支払いモーダル・受領書添付モーダル */
import { useEffect, useMemo, useState } from 'react';
import { formatCashAccountLabel } from '@core/lib/cashAccountLabel';
import { useNavigate } from 'react-router-dom';
import Modal from '@core/components/Modal';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import { todayStr } from '@core/lib/date';
import { weekendOrHoliday } from '@core/lib/jpHolidays';
import DateField from '@core/components/DateField';
import { formatYen, parseAmount } from '@core/lib/money';
import { getFileUrl, uploadFiles, validateFile } from '@core/expense/attachments';
import AttachmentThumbs from '@core/expense/AttachmentThumbs';
import FilePreviewModal from '@core/expense/FilePreviewModal';
import FileDropZone from '@core/expense/FileDropZone';
import { fetchCashAccounts } from '@core/cash/cashRepo';
import { debitDateFor, debitRuleLabel, hasDebitRule } from '@core/lib/cardDebit';
import type { StoredFile } from '@core/lib/types';
import { confirmReceive, pay, settleReceive, type PaymentInput } from './flowActions';
import { FlowPermissionError, isFlowStaleError, reportFlowResult } from './flowResult';
import {
  detailedStatusLabel,
  progressStep,
  type CashAccount,
  type CashAccountKind,
  type PaymentMethod,
  type Transaction,
} from '@core/lib/types';

/** 申請→確認→承認→支払→完了 の5ドット。差戻しは先頭が赤 */
export function StepDots({ status }: { status: Transaction['status'] }) {
  const step = progressStep(status);
  return (
    <span className="steps" title={step === -1 ? '差戻し' : '申請→確認→承認→支払→完了'}>
      {[1, 2, 3, 4, 5].map((i) => (
        <i key={i} className={step === -1 ? (i === 1 ? 'ng' : '') : i <= step ? 'on' : ''} />
      ))}
    </span>
  );
}

export function StatusBadge({
  t,
}: {
  /** 全項目を渡すと詳細ラベル(仮払い待ち(経理)等)、最小構成なら従来ラベル */
  t: Pick<Transaction, 'status' | 'doc_type'> &
    Partial<
      Pick<
        Transaction,
        'payment_method' | 'payer' | 'advance' | 'reimbursed_at' | 'settlement_received_at' | 'source' | 'report_accounting_confirmed_at'
      >
    >;
}) {
  const cls =
    t.status === 'completed'
      ? 'b-ok'
      : t.status === 'rejected'
        ? 'b-ng'
        : t.status === 'applied' || t.status === 'cancelled'
          ? 'b-plan'
          : t.status === 'paid' && !(t.advance === true && (!t.reimbursed_at || !t.settlement_received_at))
            ? 'b-ok'
            : 'b-pend';
  const label = detailedStatusLabel({
    payment_method: t.payment_method ?? null,
    payer: t.payer ?? null,
    advance: t.advance,
    reimbursed_at: t.reimbursed_at ?? null,
    settlement_received_at: t.settlement_received_at ?? null,
    status: t.status,
    doc_type: t.doc_type,
    source: t.source,
    report_accounting_confirmed_at: t.report_accounting_confirmed_at ?? null,
  });
  return <span className={`badge ${cls}`}>{label}</span>;
}

/**
 * 証憑のサムネイル一覧(2026-08-26)。画像はサムネ表示、PDFはタイル表示。
 * クリックすると画面内ポップアップで拡大プレビューする(立替精算モーダル等で使用)
 */
export function ReceiptThumbs({ files }: { files: StoredFile[] }) {
  const [urls, setUrls] = useState<Map<string, string>>(new Map());
  const [preview, setPreview] = useState<StoredFile | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all(
      files
        .filter((f) => f.content_type.startsWith('image/'))
        .map(async (f) => [f.path, await getFileUrl(f.path)] as const),
    )
      .then((pairs) => alive && setUrls(new Map(pairs)))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [files]);

  if (files.length === 0) return <span className="hint">なし</span>;
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {files.map((f, i) => {
          const url = urls.get(f.path);
          return (
            <div key={`${f.path}-${i}`} style={{ width: 92, textAlign: 'center' }}>
              {url ? (
                <img
                  src={url}
                  alt={f.name}
                  title="クリックで拡大"
                  onClick={() => setPreview(f)}
                  style={{
                    width: 92,
                    height: 68,
                    objectFit: 'cover',
                    borderRadius: 6,
                    border: '1px solid var(--line)',
                    background: '#fff',
                    cursor: 'zoom-in',
                  }}
                />
              ) : (
                <div
                  title="クリックで表示"
                  onClick={() => setPreview(f)}
                  style={{
                    width: 92,
                    height: 68,
                    borderRadius: 6,
                    border: '1px solid var(--line)',
                    background: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    color: 'var(--muted)',
                    cursor: 'zoom-in',
                  }}
                >
                  {f.content_type === 'application/pdf' ? 'PDF' : '…'}
                </div>
              )}
              <div
                className="hint"
                style={{ fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={f.name}
              >
                {f.name}
              </div>
            </div>
          );
        })}
      </div>
      {preview && <FilePreviewModal file={preview} onClose={() => setPreview(null)} />}
    </>
  );
}

/**
 * 受領書添付モーダル(v1.1・受領ページと申請一覧で共用)。
 * mode='receive': 仮払い金を受け取った記録(記入済み受領書の添付必須・§3-C-5)
 * mode='settle' : 立替精算後、本人が記入済み受領書を添付(§3-B-6。これで完了確認に進める)
 */
export function ReceiptAttachModal({
  transaction,
  mode,
  onClose,
  onDone,
}: {
  transaction: Transaction;
  mode: 'receive' | 'settle';
  onClose: () => void;
  onDone: () => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const isReceive = mode === 'receive';

  async function submit() {
    if (!user) return;
    if (files.length === 0) {
      toast('記入済みの受領書の添付が必要です');
      return;
    }
    for (const f of files) {
      const err = validateFile(f);
      if (err) {
        toast(err);
        return;
      }
    }
    setBusy(true);
    try {
      const receipts = await uploadFiles(
        transaction.id,
        isReceive ? 'received_receipts' : 'settlement_receipts',
        files,
        user.id,
      );
      if (isReceive) {
        const result = await confirmReceive({ transaction, userId: user.id, receipts });
        reportFlowResult(toast, `${transaction.code} の受領を記録しました。支払い後に「支払う」で実績計上してください`, result);
      } else {
        const result = await settleReceive({ transaction, userId: user.id, receipts });
        reportFlowResult(toast, `${transaction.code} の精算受領書を添付しました。承認者の完了確認に進みます`, result);
      }
      onDone();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : '受領の記録に失敗しました');
      // ほかの人が先に処理していた場合は、一覧を最新にして閉じる
      if (isFlowStaleError(e)) {
        onDone();
        onClose();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} width="min(560px, 92vw)">
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <h3>{isReceive ? '受領を記録(仮払い金の受け取り)' : '精算受領書を添付'}</h3>
          <div className="path" style={{ margin: '2px 0 0' }}>
            {transaction.code} / {formatYen(transaction.amount)}円
          </div>
        </div>
      </div>
      <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
        <div>
          <label>
            記入済みの受領書<span className="req">必須</span> — 電子署名か、署名した紙のスキャン・撮影を添付
          </label>
          {/* 電子署名(2026-08-26 押印方式): 受領書ページを開き、内容確認→押印→そのまま添付・記録 */}
          <button
            type="button"
            className="btn-green"
            style={{ marginTop: 0, marginBottom: 10 }}
            disabled={busy}
            onClick={() => navigate(`/receipt/${transaction.id}?mode=internal&sign=${mode}`)}
          >
            電子署名する(受領書を開いて押印 — 印刷・スキャン不要)
          </button>
          <FileDropZone files={files} onChange={setFiles} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn-green" onClick={() => void submit()} disabled={busy || files.length === 0}>
          {busy ? '処理中…' : isReceive ? '受領を記録' : '添付して完了確認へ進める'}
        </button>
        {/* タブ単位ログインのため別タブでは開かず、同じタブで受領書ページへ移動する(「戻る」で復帰) */}
        <button className="txn-link" onClick={() => navigate(`/receipt/${transaction.id}?mode=internal`)}>
          受領書を出力(未記入の場合)
        </button>
      </div>
      <p className="hint" style={{ marginTop: 8 }}>
        {isReceive
          ? '経理から受け取った受領書に記入し、その控えを添付してください。記録後、支払いを実行して「支払う」で実績計上します。'
          : '経理から精算金を受け取ったことを示す記入済み受領書を添付してください。添付すると承認者の完了確認に進みます。'}
      </p>
    </Modal>
  );
}

/** 口座マスタの種別 → 取引の支払方法区分 */
function methodOfKind(kind: CashAccountKind): PaymentMethod {
  return kind === 'cash' ? 'cash' : kind === 'card' ? 'card' : 'bank_transfer';
}

/** 入力中の金額文字列を3桁カンマ付きに整形する */
function formatAmountInput(raw: string): string {
  const digits = raw
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/\D/g, '');
  return digits === '' ? '' : Number(digits).toLocaleString('ja-JP');
}

/**
 * 支払いモーダル(approved → paid)。
 * 支払日・支払い方法(口座マスタから選択)・確定金額(税込)・証憑(必須)。
 * 経理は請求書の経理入力欄(科目・計上日)も入力する。
 * CFへの計上は経理支払いのときのみ(自分・部門長の立替は会社口座が動かないため)。
 */
export function PaymentModal({
  transaction,
  onClose,
  onDone,
}: {
  transaction: Transaction;
  onClose: () => void;
  onDone: () => void;
}) {
  const { user } = useAuth();
  const { accounts, subAccountsOf, companyName } = useMasters();
  const toast = useToast();
  const navigate = useNavigate();

  const isInvoice = transaction.doc_type === 'invoice';
  // 仮払いは仮払い額=確定金額(差額なし運用)。会社の口座も動かないため方法選択なし(v1.1 §3-C)
  const isPrepaid = transaction.payment_method === 'prepaid';
  // 立替は自分のお金で支払う(現金かクレカかは本人に任せる)ため、会社の口座は選ばない(2026-08-26)
  const isReimb = transaction.payment_method === 'reimbursement' || transaction.advance === true;
  const noCompanyAccount = isPrepaid || isReimb;
  /** 繰り返し支払い(2026-09-07): 口座引落・振込の定期支払い。明細の添付は任意、完全固定なら金額は変えない */
  const isRecurring = transaction.source === 'recurring';
  /** 現金払い(支払者は必ず経理)。領収書の添付を任意にする(2026-09-17 改修依頼 D・解釈1で確定) */
  const isCashByAccounting = transaction.payment_method === 'cash' && transaction.payer === 'accounting';
  /**
   * 申請時の添付(2026-10-14 指示)。どの支払いでも支払い画面に参考として表示する。
   * 立替は、申請時に添付した領収書をそのまま支払いの証憑として引き継げるので、ここでの添付は任意。
   * 新しく添付した場合はそちらを証憑にする(差し替え)。
   * 経理の振込などは、申請時の添付(請求書)と支払いの証憑(振込明細)が別の書類なので、従来どおり添付が必要
   */
  const appAttachments = transaction.attachments ?? [];
  const carryOver = isReimb && appAttachments.length > 0;
  const needsAccountInput = isInvoice && !transaction.account_id;
  /**
   * 科目①の候補(2026-09-28 指示で修正)。判定は経費申請とまったく同じにする:
   * マスタ「表示する事業部」にこの申請の事業部が入っている科目を出す(「その他」事業部は全部出す)。
   * 以前は「PL区分=経費」だけに絞っていたため、原価の科目(仕入・外注費など)が選べなかった
   */
  const expenseAccounts = useMemo(
    () =>
      accounts
        .filter(
          (a) =>
            a.active &&
            (a.department_ids?.length ?? 0) > 0 &&
            (transaction.department_id === 'sonota' || a.department_ids!.includes(transaction.department_id)),
        )
        .sort((a, b) => a.display_order - b.display_order),
    [accounts, transaction.department_id],
  );

  // 繰り返し支払いは予定の支払日(土日祝をずらした日)を最初から入れておく。クレカは利用日(引き落とし日はカードの設定から計算)
  const [paymentDate, setPaymentDate] = useState(
    isRecurring
      ? transaction.payment_method === 'card' && transaction.purchase_date
        ? transaction.purchase_date
        : transaction.payment_date
      : todayStr(),
  );
  const [files, setFiles] = useState<File[]>([]);
  const [cashAccounts, setCashAccounts] = useState<CashAccount[]>([]);
  const [paymentAccountId, setPaymentAccountId] = useState('');
  const [amountText, setAmountText] = useState(formatAmountInput(String(transaction.amount)));
  const [accountId, setAccountId] = useState(transaction.account_id);
  const [subAccountId, setSubAccountId] = useState(transaction.sub_account_id);
  const [postingDate, setPostingDate] = useState(transaction.posting_date);
  const [busy, setBusy] = useState(false);
  /** 立替の金額差異の警告(2026-08-26: OSのconfirmでなくシステム内の赤色モーダルで表示) */
  const [amountWarning, setAmountWarning] = useState<{ applied: number; current: number } | null>(null);
  /** 振込×支払日が土日祝のときの確認(■13・2026-08-28) */
  const [holidayWarning, setHolidayWarning] = useState<string | null>(null);
  /** 振込手数料(2026-08-26 会議): 振込×経理のとき入力可。「支払手数料」科目で自動計上 */
  const [feeText, setFeeText] = useState('');
  const showFee = !noCompanyAccount && transaction.payer === 'accounting' && transaction.payment_method === 'bank_transfer';
  const feeAccount = accounts.find((a) => a.active && a.name === '支払手数料') ?? null;

  // 会社の口座は「申請の会社」かつ「申請時の支払い方法と同じ種別」だけを候補にする
  // (例: KMAの申請にSUMの楽天銀行は出さない。会社「その他」の申請のみ全社表示。2026-08-26)
  const txCompany = transaction.company_id ?? 'sum';
  const activeAccounts = useMemo(() => {
    const applied = transaction.payment_method;
    return cashAccounts.filter(
      (a) =>
        a.active &&
        (txCompany === 'sonota' || a.company_id === txCompany) &&
        // 口座引落(繰り返し支払い)は銀行口座から落ちる(2026-09-07)
        (applied == null || methodOfKind(a.kind) === applied || (applied === 'direct_debit' && a.kind === 'bank')),
    );
  }, [cashAccounts, transaction.payment_method, txCompany]);

  // クレカ(2026-09-04 指示): 「支払日」は「購入日」。引き落とし日はカードの締め日・引き落とし日から自動で決まる
  const selectedAccount = activeAccounts.find((a) => a.id === paymentAccountId) ?? null;
  const isCard = selectedAccount?.kind === 'card' || (!selectedAccount && transaction.payment_method === 'card');
  // 引き落とし日はカードの締め日・引き落とし日と、土日祝の設定(2026-09-08)から自動
  const debitDate = isCard && selectedAccount ? debitDateFor(paymentDate, selectedAccount) : null;

  useEffect(() => {
    fetchCashAccounts()
      .then((list) => {
        setCashAccounts(list);
        const applied = transaction.payment_method;
        // 繰り返し支払いは設定した口座を最初から選んでおく(2026-09-07)
        const preset = transaction.payment_account_id
          ? list.find((a) => a.id === transaction.payment_account_id && a.active)
          : undefined;
        const match =
          preset ??
          list.find(
            (a) =>
              a.active &&
              (txCompany === 'sonota' || a.company_id === txCompany) &&
              applied != null &&
              (methodOfKind(a.kind) === applied || (applied === 'direct_debit' && a.kind === 'bank')),
          );
        if (match) setPaymentAccountId((prev) => prev || match.id);
      })
      .catch(() => setCashAccounts([]));
  }, [transaction.payment_method, transaction.payment_account_id, txCompany]);

  async function submit(diffConfirmed = false, holidayOk = false) {
    if (!user) return;
    const amount = parseAmount(amountText);
    if (amount === null) {
      toast('確定金額を正しく入力してください');
      return;
    }
    // 振込で支払日が土日祝の場合は確認ポップアップ(■13・2026-08-28)
    if (transaction.payment_method === 'bank_transfer' && !holidayOk) {
      const hit = weekendOrHoliday(paymentDate);
      if (hit) {
        setHolidayWarning(hit.label);
        return;
      }
    }
    if (!noCompanyAccount && !paymentAccountId) {
      toast('支払い方法(どの口座・現金か)を選んでください');
      return;
    }
    if (isPrepaid && amount !== transaction.amount) {
      toast('仮払いは仮払い額と同額で支払います(差額なし運用)');
      return;
    }
    // 承認時より金額が「多い」場合のみ赤色警告(2026-08-26 会議。少ない場合は警告なし・通常表示)
    const appliedAmount = transaction.applied_amount ?? transaction.amount;
    // 経理の支払い画面では金額差異の警告を出さない(2026-08-31 指示)。
    // 立替を本人が支払うときだけ、承認額より多い場合に確認する
    if (isReimb && transaction.payer !== 'accounting' && amount > appliedAmount && !diffConfirmed) {
      setAmountWarning({ applied: appliedAmount, current: amount });
      return;
    }
    if (files.length === 0 && !isRecurring && !isCashByAccounting && !carryOver) {
      toast(
        isPrepaid || transaction.payment_method === 'cash'
          ? '署名済み受領書または領収書の添付が必要です'
          : isReimb
            ? '領収書(立替払いの証憑)の添付が必要です'
            : '振込明細または領収書の添付が必要です',
      );
      return;
    }
    for (const f of files) {
      const err = validateFile(f);
      if (err) {
        toast(err);
        return;
      }
    }
    const selected = activeAccounts.find((a) => a.id === paymentAccountId);
    if (!noCompanyAccount && !selected) {
      toast('支払い方法を選び直してください');
      return;
    }

    setBusy(true);
    try {
      const uploaded = await uploadFiles(transaction.id, 'receipts', files, user.id);
      // 立替で新しく添付しなかったときは、申請時の領収書を証憑として引き継ぐ
      const receipts = uploaded.length > 0 ? uploaded : carryOver ? appAttachments : uploaded;
      // 仮払い・立替は申請時の支払方法(契約)を保持し、会社の口座は記録しない(個人のお金のため)
      // 振込手数料(2026-09-16 改修依頼 E): 空欄=未入力(null)、0 と入力=0円 として区別して保存する。
      // 以前は空欄も0も「なし」に丸めていたため、入力忘れか0円かが分からなかった
      const feeDigits = feeText.replace(/[^0-9]/g, '');
      const fee = showFee && feeDigits !== '' ? Number(feeDigits) : null;
      const input: PaymentInput = {
        // クレカ: 支払日(CF)=引き落とし日、購入日=PL計上日(2026-09-04)。設定がなければ購入日のまま
        paymentDate: isCard ? (debitDate ?? paymentDate) : paymentDate,
        purchaseDate: isCard ? paymentDate : null,
        paymentMethod: noCompanyAccount
          ? (transaction.payment_method ?? 'reimbursement')
          : isRecurring
            ? (transaction.payment_method ?? methodOfKind(selected!.kind))
            : methodOfKind(selected!.kind),
        paymentAccountId: noCompanyAccount ? null : paymentAccountId,
        receipts,
        // CFに計上するのは経理支払いのときのみ(会社の口座・現金が実際に動くため)
        // 会社の口座・現金・カードを使った支払いはCFへ出金を記録する(2026-09-07: クレカは支払い者が
        // 社員でも会社のお金なので対象。引き落とし日の「予定」として記録)
        cashAccountId: !noCompanyAccount ? paymentAccountId : null,
        cashEntryStatus: isCard ? 'planned' : 'settled',
        amount,
        accountId: accountId || undefined,
        subAccountId: subAccountId || undefined,
        // 繰り返し支払いのクレカは計上日を設定どおりに保つ(購入日=計上日にはしない)
        postingDate: isCard && !isRecurring ? paymentDate : postingDate || undefined,
        // 0円も記録する(手数料の別取引・資金繰りへの出金は 1円以上のときだけ作られる)
        transferFee: fee,
        feeAccountId: feeAccount?.id ?? null,
      };
      const result = await pay({ transaction, input, userId: user.id });
      reportFlowResult(toast, `${transaction.code} を支払い済みにしました。実績に計上されます`, result);
      onDone();
      onClose();
    } catch (e) {
      // 権限ルールの反映前に「現金・領収書なし」で支払った場合は、分かる言葉で案内する(2026-09-17 改修依頼 D)
      if (e instanceof FlowPermissionError && isCashByAccounting && files.length === 0) {
        toast('現金払いの領収書を任意にする設定がまだ反映されていません。お手数ですが添付して支払い済みにしてください');
      } else {
        toast(e instanceof Error ? e.message : '支払いに失敗しました');
      }
      // ほかの人が先に処理していた場合は、一覧を最新にして閉じる
      if (isFlowStaleError(e)) {
        onDone();
        onClose();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} width="min(640px, 92vw)">
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <h3>支払い方法を入力する</h3>
          <div className="path" style={{ margin: '2px 0 0' }}>
            {transaction.code} / 予定 {formatYen(transaction.applied_amount ?? transaction.amount)}円 /{' '}
            {transaction.description}
          </div>
        </div>
      </div>

      <div>
        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          <div>
            <label>
              {isCard
                ? isRecurring
                  ? '利用日(カードで払った日)'
                  : '購入日(＝計上日)'
                : isRecurring && transaction.payment_method === 'direct_debit'
                  ? '引き落とし日'
                  : '支払日(取引日)'}
              <span className="req">必須</span>
            </label>
            <DateField value={paymentDate} onChange={setPaymentDate} />
          </div>
          {!noCompanyAccount && (
            <div>
              <label>
                支払い方法<span className="req">必須</span>
              </label>
              <select value={paymentAccountId} onChange={(e) => setPaymentAccountId(e.target.value)}>
                <option value="">選択してください</option>
                {activeAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {formatCashAccountLabel(a, companyName(a.company_id))}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            {/* 注記は削除(2026-08-27。日付欄と段が揃うようラベルを1行に) */}
            <label>
              確定金額(税込)<span className="req">必須</span>
            </label>
            <input
              inputMode="numeric"
              value={amountText}
              disabled={isPrepaid}
              onChange={(e) => setAmountText(formatAmountInput(e.target.value))}
            />
            {isRecurring && (
              <div className="hint" style={{ marginTop: 3 }}>
                繰り返し支払いの設定の金額が入っています。実際に支払った(引き落とされた)金額が違うときは書き換えてください。
              </div>
            )}
          </div>
          {/* クレカの引き落とし日(2026-09-04 指示): 選んだカードの締め日・引き落とし日から自動計算。変更不可 */}
          {isCard && (
            <div>
              <label>引き落とし日(自動)</label>
              <input
                value={debitDate ? debitDate.replace(/-/g, '/') : ''}
                readOnly
                disabled
                placeholder={selectedAccount && !hasDebitRule(selectedAccount) ? 'カードの締め日・引き落とし日が未設定' : 'カードを選ぶと表示'}
                style={{ background: '#EEF2F8', color: 'var(--ink)' }}
              />
              {selectedAccount && (
                <div className="hint" style={{ marginTop: 3 }}>
                  {hasDebitRule(selectedAccount)
                    ? `${selectedAccount.name}: ${debitRuleLabel(selectedAccount)}`
                    : 'マスタ管理→支払い方法で、このカードの締め日・引き落とし日を設定してください'}
                </div>
              )}
            </div>
          )}
          {/* 振込手数料(2026-08-26 会議): 振込×経理のみ。「支払手数料」科目・同じ事業部で自動計上 */}
          {showFee && (
            <div>
              <label>振込手数料(任意)</label>
              <input
                inputMode="numeric"
                value={feeText}
                placeholder="例)330"
                onChange={(e) => setFeeText(formatAmountInput(e.target.value))}
              />
              {!feeAccount && (
                <div className="hint" style={{ color: 'var(--red)' }}>
                  科目「支払手数料」が見つからないため自動計上できません(マスタ管理で有効にしてください)
                </div>
              )}
            </div>
          )}
          {appAttachments.length > 0 && (
            <div className="fw">
              <label>
                申請時の添付
                <span className="hint">{carryOver ? '(この領収書を支払いの証憑として引き継ぎます)' : '(参考)'}</span>
              </label>
              <AttachmentThumbs files={appAttachments} />
            </div>
          )}
          <div className="fw">
            <label>
              {isPrepaid || transaction.payment_method === 'cash'
                ? '署名済み受領書・領収書'
                : isReimb
                  ? '領収書(立替払いの証憑)'
                  : isRecurring
                    ? '振込明細・引き落とし明細'
                    : '振込明細・領収書'}
              {carryOver ? (
                <span className="hint">(任意 — 申請時の添付を引き継ぎます。差し替えるときだけ添付)</span>
              ) : isRecurring || isCashByAccounting ? (
                <span className="hint">(任意)</span>
              ) : (
                <span className="req">必須</span>
              )}
            </label>
            <FileDropZone files={files} onChange={setFiles} />
            {/* 仮払い: 紙の受領書のスキャン添付のほか、その場でタブレット・スマホに
                支払先のサインをもらって添付→支払い済みにできる(2026-08-26) */}
            {isPrepaid && (
              <div style={{ marginTop: 8 }}>
                <button
                  className="btn-line"
                  style={{ borderColor: 'var(--green)', color: 'var(--green)', fontWeight: 600 }}
                  onClick={() =>
                    navigate(`/receipt/${transaction.id}?sign=pay&date=${paymentDate}`)
                  }
                >
                  または: 支払先にその場でサインをもらう(タブレット・スマホ)
                </button>
                <div className="hint" style={{ marginTop: 4 }}>
                  受領書ページが開き、支払先が画面に手書きサイン→署名入りPDFを添付して支払い済みにできます
                </div>
              </div>
            )}
          </div>
        </div>

        {needsAccountInput && (
          <div className="gray-box" style={{ background: '#fff' }}>
            <div className="box-title">経理入力欄 — 科目と計上日を入力してください</div>
            <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <div>
                <label>
                  科目①<span className="req">必須</span>
                </label>
                <select
                  value={accountId}
                  onChange={(e) => {
                    setAccountId(e.target.value);
                    setSubAccountId('');
                  }}
                >
                  <option value="">選択してください</option>
                  {expenseAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>科目②(任意)</label>
                <select value={subAccountId} disabled={!accountId} onChange={(e) => setSubAccountId(e.target.value)}>
                  <option value="">—</option>
                  {(accountId ? subAccountsOf(accountId) : []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>
                  計上日<span className="req">必須</span>
                </label>
                {isCard ? (
                  // クレカは計上日＝購入日(2026-09-07)。別々に入力できると取り違えるため、ここでは入力させない
                  <input value={paymentDate ? `${paymentDate.replace(/-/g, '/')}(購入日と同じ)` : '購入日と同じ'} readOnly disabled style={{ background: '#EEF2F8', color: 'var(--ink)' }} />
                ) : (
                  <input type="date" value={postingDate} onChange={(e) => setPostingDate(e.target.value)} />
                )}
              </div>
            </div>
          </div>
        )}

        {isPrepaid ? (
          <p className="hint" style={{ marginTop: 8 }}>
            支払先から受け取った証憑を添付してください。
          </p>
        ) : isReimb ? (
          <p className="hint" style={{ marginTop: 8 }}>
            立替のため自分のお金で支払います(現金・個人カード等は自由)。会社の口座は選びません。
          </p>
        ) : isRecurring ? (
          <p className="hint" style={{ marginTop: 8 }}>
            {transaction.payment_method === 'direct_debit'
              ? '繰り返し支払い(口座引落)です。引き落としを確認したら確認済みにしてください。実績に載り、CFの支払予定が確定に変わります。その後、事業部の部門長の確認、承認者の完了確認を経てロックされます。'
              : transaction.payment_method === 'card'
                ? '繰り返し支払い(クレジットカード)です。カードの引き落としを確認したら確認済みにしてください。計上日(PL)は設定どおり、CFは引き落とし日で確定します。その後、事業部の部門長の確認、承認者の完了確認を経てロックされます。'
                : transaction.payment_method === 'cash'
                  ? '繰り返し支払い(現金)です。現金で支払ったら支払い済みにしてください。実績に載り、CFの支払予定が確定に変わります。その後、事業部の部門長の確認、承認者の完了確認を経てロックされます。'
                  : '繰り返し支払い(振込)です。振り込んだら支払い済みにしてください。実績に載り、CFの支払予定が確定に変わります。その後、事業部の部門長の確認、承認者の完了確認を経てロックされます。'}
          </p>
        ) : null}
      </div>

      <div style={{ paddingTop: 12 }}>
        <button className="btn-green" style={{ marginTop: 0 }} onClick={() => void submit()} disabled={busy}>
          {busy
            ? '処理中…'
            : isRecurring && (transaction.payment_method === 'direct_debit' || transaction.payment_method === 'card')
              ? 'この内容で引き落とし確認済みにする(実績計上)'
              : 'この内容で支払い済みにする(実績計上)'}
        </button>
      </div>

      {/* 振込×土日祝の確認(■13・2026-08-28) */}
      {holidayWarning && (
        <Modal onClose={() => setHolidayWarning(null)} width="min(480px, 92vw)" showClose={false}>
          <div style={{ border: '2px solid var(--amber)', background: 'var(--amber-bg)', borderRadius: 8, padding: '16px 18px' }}>
            <div style={{ color: 'var(--amber)', fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
              ⚠ 支払日が{holidayWarning}です
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.9 }}>
              支払日({paymentDate.replace(/-/g, '/')})は土日祝日ですが、この日付でよろしいですか?
              銀行振込は土日祝日に着金しない場合があります。
            </div>
            {/* ボタン内で文字が折り返さないようにする(2026-08-28 指示)。狭い画面ではボタンごと縦に積む */}
            <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
              <button
                className="btn-green"
                style={{ marginTop: 0, whiteSpace: 'nowrap' }}
                disabled={busy}
                onClick={() => {
                  setHolidayWarning(null);
                  void submit(false, true);
                }}
              >
                この日付で登録する
              </button>
              <button className="btn-line" style={{ whiteSpace: 'nowrap' }} onClick={() => setHolidayWarning(null)}>
                日付を修正する
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* 金額差異の警告(立替)。赤色で目立たせ、承認者への確認を促す */}
      {amountWarning && (
        <Modal onClose={() => setAmountWarning(null)} width="min(520px, 92vw)" showClose={false}>
          <div
            style={{
              border: '2px solid var(--red)',
              background: 'var(--red-bg)',
              borderRadius: 8,
              padding: '16px 18px',
            }}
          >
            <div style={{ color: 'var(--red)', fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
              ⚠ 承認時の金額と異なります
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.9 }}>
              承認時の金額: <b>{formatYen(amountWarning.applied)}円</b>
              <br />
              今回の支払い金額: <b style={{ color: 'var(--red)' }}>{formatYen(amountWarning.current)}円</b>
              <br />
              <span style={{ color: 'var(--red)', fontWeight: 700 }}>
                承認者の確認を取ってください。
              </span>
              このまま登録すると、申請一覧に申請時の金額が赤字で併記されます。
            </div>
            {/* ボタン内で文字が折り返さないようにする(2026-08-28 指示)。狭い画面ではボタンごと縦に積む */}
            <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
              <button
                className="btn-sm-ng"
                style={{ whiteSpace: 'nowrap' }}
                disabled={busy}
                onClick={() => {
                  setAmountWarning(null);
                  void submit(true, true);
                }}
              >
                確認済み — このまま支払いを登録する
              </button>
              <button className="btn-line" style={{ whiteSpace: 'nowrap' }} onClick={() => setAmountWarning(null)}>
                戻って修正する
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
}
